/**
 * Iter 84.3 / Phase E5 Sprint 3 — TDS Reconciliation Console.
 *
 * D38-C — reconciliation status: NOT_RECONCILED · PARTIAL · MATCHED · EXCEPTION
 * D38-D — missing PAN is WARNING; PAN format invalid is ERROR
 */
import { useCallback, useEffect, useState } from "react";
import {
    ArrowsClockwise, DownloadSimple, Warning, CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";

const fmtMoney = (v) =>
    v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_COLORS = {
    NOT_RECONCILED: "bg-slate-100 text-slate-700",
    PARTIAL: "bg-amber-100 text-amber-800",
    MATCHED: "bg-emerald-100 text-emerald-700",
    EXCEPTION: "bg-rose-100 text-rose-700",
};

const CATEGORY_COLORS = {
    missing_pan: "bg-amber-50 text-amber-700",
    pan_format_invalid: "bg-rose-100 text-rose-700",
    tds_deducted_vs_filed_mismatch: "bg-rose-50 text-rose-700",
    tds_filed_vs_deposited_mismatch: "bg-amber-50 text-amber-700",
    unmatched_challan: "bg-blue-50 text-blue-700",
};

export default function TdsReconciliationConsole() {
    const [fy, setFy] = useState("2025-2026");
    const [quarter, setQuarter] = useState("Q1");
    const [data, setData] = useState(null);
    const [statusRows, setStatusRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadStatusList = useCallback(async () => {
        try {
            const { data } = await api.get("/compliance/tds/reconciliation/status");
            setStatusRows(data.items || []);
        } catch (err) {
            // non-fatal
        }
    }, []);

    useEffect(() => { loadStatusList(); }, [loadStatusList]);

    const handleReconcile = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/compliance/tds/reconciliation?financial_year=${fy}&quarter=${quarter}`);
            setData(data);
            toast.success(`Reconciliation complete · ${data.summary.status}`);
            loadStatusList();
        } catch (err) {
            toast.error(formatApiError(err, "Reconciliation failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleExportCsv = async () => {
        setLoading(true);
        try {
            const r = await api.get(
                `/compliance/tds/reconciliation/export.csv?financial_year=${fy}&quarter=${quarter}`,
                { responseType: "blob" },
            );
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `TDS_Reconciliation_${fy}_${quarter}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Reconciliation CSV downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "CSV export failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="tds-recon-console">
            <PageHeader
                title="TDS Reconciliation"
                subtitle="Cross-check Payroll TDS vs Form 24Q filings vs Deposited Challans · D38-C status · D38-D PAN-error gating"
                actions={
                    <Button size="sm" variant="outline" onClick={loadStatusList} data-testid="tds-recon-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            <div className="border rounded p-4 bg-slate-50/30 flex items-end gap-3" data-testid="tds-recon-controls">
                <div>
                    <Label className="text-xs">Financial Year</Label>
                    <Input value={fy} onChange={(e) => setFy(e.target.value)} className="font-mono w-32" data-testid="tds-recon-fy-input" />
                </div>
                <div>
                    <Label className="text-xs">Quarter</Label>
                    <select value={quarter} onChange={(e) => setQuarter(e.target.value)} className="border rounded px-2 py-1 text-sm font-mono" data-testid="tds-recon-quarter-input">
                        <option value="Q1">Q1 (Apr-Jun)</option>
                        <option value="Q2">Q2 (Jul-Sep)</option>
                        <option value="Q3">Q3 (Oct-Dec)</option>
                        <option value="Q4">Q4 (Jan-Mar)</option>
                    </select>
                </div>
                <Button size="sm" onClick={handleReconcile} disabled={loading} data-testid="tds-recon-run-btn">
                    Run Reconciliation
                </Button>
                {data && (
                    <Button size="sm" variant="outline" onClick={handleExportCsv} disabled={loading} data-testid="tds-recon-export-btn">
                        <DownloadSimple size={14} className="mr-1" /> Export CSV
                    </Button>
                )}
            </div>

            {/* Status overview across all FY+Quarters */}
            {statusRows.length === 0 && !data && (
                <div className="border rounded-xl bg-white p-8 text-center" data-testid="tds-recon-empty-state">
                    <ArrowsClockwise size={40} className="mx-auto text-slate-300 mb-3" />
                    <div className="text-base font-semibold text-slate-800">
                        No TDS reconciliation has been computed yet
                    </div>
                    <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                        Reconciliation cross-checks <b>Payroll TDS</b> against filed <b>Form 24Q</b> returns and deposited <b>challans</b>. Select an FY + quarter above and click <b>Run Reconciliation</b> to compute variances.
                    </div>
                    <div className="text-xs text-slate-400 mt-3 max-w-md mx-auto">
                        Prerequisites: at least one locked payroll run in the quarter · at least one filed 24Q return · challans loaded via the TDS Challans console.
                    </div>
                    <Button size="sm" className="mt-4" onClick={handleReconcile} disabled={loading} data-testid="tds-recon-empty-run-btn">
                        Run Reconciliation for {fy} {quarter}
                    </Button>
                </div>
            )}
            {statusRows.length > 0 && (
                <div className="border rounded">
                    <div className="p-2 bg-slate-50 border-b text-xs font-semibold">Status Overview · {statusRows.length} period(s)</div>
                    <table className="w-full text-xs" data-testid="tds-recon-status-table">
                        <thead className="bg-slate-50 border-b text-slate-600 uppercase text-[10px]">
                            <tr>
                                <th className="text-left px-2 py-1">FY</th>
                                <th className="text-left px-2 py-1">Q</th>
                                <th className="text-left px-2 py-1">Status</th>
                                <th className="text-right px-2 py-1">Employees</th>
                                <th className="text-right px-2 py-1">Payroll TDS</th>
                                <th className="text-right px-2 py-1">Filed</th>
                                <th className="text-right px-2 py-1">Deposited</th>
                                <th className="text-right px-2 py-1">Missing PAN</th>
                                <th className="text-right px-2 py-1">Variance #</th>
                                <th className="text-left px-2 py-1">Computed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {statusRows.map((s) => (
                                <tr key={`${s.financial_year}-${s.quarter}`} className="border-b">
                                    <td className="px-2 py-1 font-mono">{s.financial_year}</td>
                                    <td className="px-2 py-1 font-mono">{s.quarter}</td>
                                    <td className="px-2 py-1">
                                        <Badge className={STATUS_COLORS[s.status] || ""}>{s.status}</Badge>
                                    </td>
                                    <td className="px-2 py-1 text-right font-mono">{s.n_employees}</td>
                                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(s.total_payroll_tds)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(s.total_filed_tds)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(s.total_deposited)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{s.n_missing_pan}</td>
                                    <td className="px-2 py-1 text-right font-mono">{s.n_variance}</td>
                                    <td className="px-2 py-1 text-[10px] text-slate-500">{s.computed_at?.slice(0, 16).replace("T", " ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Live reconciliation result */}
            {data && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tds-recon-summary">
                        <SummaryCard label="Status" value={
                            <Badge className={STATUS_COLORS[data.summary.status] || ""}>{data.summary.status}</Badge>
                        } />
                        <SummaryCard label="Employees" value={data.summary.n_employees} />
                        <SummaryCard label="Payroll TDS" value={fmtMoney(data.summary.total_payroll_tds)} />
                        <SummaryCard label="Filed TDS" value={fmtMoney(data.summary.total_filed_tds)} />
                        <SummaryCard label="Deposited" value={fmtMoney(data.summary.total_deposited)} />
                        <SummaryCard label="Variance Count" value={data.summary.n_variance} />
                        <SummaryCard label="Missing PAN" value={data.summary.n_missing_pan} tone="amber" />
                        <SummaryCard label="PAN Invalid" value={data.summary.n_pan_invalid} tone="rose" />
                    </div>

                    <div className="border rounded">
                        <div className="p-2 bg-slate-50 border-b text-xs font-semibold flex justify-between">
                            <span>Variances · {data.variances.length}</span>
                            {data.summary.latest_24q_id && (
                                <span className="text-[10px] text-slate-500">Reconciled against 24Q filing <code className="font-mono">{data.summary.latest_24q_id}</code> ({data.summary.latest_24q_status})</span>
                            )}
                        </div>
                        <table className="w-full text-xs" data-testid="tds-recon-variances-table">
                            <thead className="bg-slate-50 border-b text-slate-600 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left px-2 py-1">Code</th>
                                    <th className="text-left px-2 py-1">Name</th>
                                    <th className="text-left px-2 py-1">PAN</th>
                                    <th className="text-left px-2 py-1">Category</th>
                                    <th className="text-right px-2 py-1">Payroll TDS</th>
                                    <th className="text-right px-2 py-1">Filed TDS</th>
                                    <th className="text-right px-2 py-1">Variance</th>
                                    <th className="text-left px-2 py-1">Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.variances.length === 0 && (
                                    <tr><td colSpan="8" className="text-center text-emerald-600 p-6 text-base">
                                        <CheckCircle size={18} className="inline mr-2" /> No variances detected — TDS reconciled cleanly
                                    </td></tr>
                                )}
                                {data.variances.map((v, i) => (
                                    <tr key={i} className="border-b" data-testid={`tds-recon-variance-${i}`}>
                                        <td className="px-2 py-1 font-mono">{v.employee_code}</td>
                                        <td className="px-2 py-1">{v.employee_name}</td>
                                        <td className="px-2 py-1 font-mono">{v.pan || <span className="text-rose-600">—</span>}</td>
                                        <td className="px-2 py-1">
                                            <Badge className={CATEGORY_COLORS[v.category] || ""}>{v.category}</Badge>
                                        </td>
                                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(v.payroll_tds)}</td>
                                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(v.filed_tds)}</td>
                                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(v.variance_amount)}</td>
                                        <td className="px-2 py-1 text-[10px] text-slate-600">{v.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function SummaryCard({ label, value, tone }) {
    return (
        <div className={`border rounded p-3 ${tone === "amber" ? "bg-amber-50" : tone === "rose" ? "bg-rose-50" : "bg-slate-50/30"}`}>
            <div className="text-[10px] uppercase text-slate-500">{label}</div>
            <div className="font-mono text-base font-semibold">{value}</div>
        </div>
    );
}
